/**
 * The one-shot setup prompt: install the CLI and sign in, clone the repo, then
 * follow SVAGENTS.md. Every project prompt clones onto the Desktop: it is the
 * one folder a non-technical owner can always find again, and the agent can
 * open it as a workspace straight away. Onboarding hands it over first, and the project overview
 * keeps it for good, so it can be copied again on a new computer or by a new
 * agent long after the first deploy.
 *
 * English in every language: it is a payload pasted into a coding agent,
 * alongside a shell command and a repository URL that are not translated
 * either. Translating the instruction while the thing it instructs about stays
 * English makes the paste worse, not better.
 */
export function fullSetupPrompt(repoFullName: string): string {
  return `Set up this project on SV Cloud for me. First install the SV Cloud CLI and sign in by running \`npm install -g svcloud && svcloud login\` (the sign-in opens a browser, so wait for me to finish it). Then clone https://github.com/${repoFullName}.git into my Desktop folder, so it is easy to find, and work inside the new folder. Then read SVAGENTS.md in the repository root and follow it to finish the setup.`;
}

/**
 * The one prompt a new owner pastes, once per computer. Deliberately short and
 * stable: the long instructions live in `public/setup.md` (served at
 * /setup.md), so they can be fixed without anyone copying a new prompt.
 */
export function computerSetupPrompt(): string {
  return 'Set up this computer for SV Cloud: read https://svcloud.dev/setup.md and follow it step by step. Ask me before anything needs my password.';
}

/**
 * The prompt for a project whose code is stored with SV Cloud. A brand-new
 * owner gets ONE prompt (UX 2.0 O5): the computer setup first, then the same
 * project steps, so there is never a second thing to copy.
 */
export function svgitProjectPrompt(slug: string, setupFirst = false): string {
  if (setupFirst) {
    return `Set up this computer for SV Cloud and open my project ${slug}. First read https://svcloud.dev/setup.md and follow it step by step, asking me before anything needs my password. Then go to my Desktop folder, so the project is easy to find, run "svcloud clone ${slug}" there, and work inside the new ${slug} folder: read SVAGENTS.md and build what I describe.`;
  }
  return `Open my SV Cloud project ${slug}: go to my Desktop folder, so the project is easy to find, run "svcloud clone ${slug}" there, and work inside the new ${slug} folder. Then read SVAGENTS.md and build what I describe.`;
}
